import { baseApi } from './baseApi';

export type NotificationType = 'info' | 'success' | 'warning' | 'error' | 'system';
export type NotificationChannel = 'in_app' | 'email' | 'sms' | 'push';

export interface Notification {
  id: string;
  userId: string;
  tenantId?: string;
  title: string;
  message: string;
  type: NotificationType;
  channels: NotificationChannel[];
  isRead: boolean;
  sent: boolean;
  link?: string | null;
  metadata: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
  deletedAt?: string | null;
}

export interface NotificationMeta {
  total: number;
  /** API returns page/limit as strings */
  page: string | number;
  limit: string | number;
  totalPages: number;
  hasMore: boolean;
}

interface NotificationsApiResponse {
  success: boolean;
  response: {
    data: Notification[];
    meta: NotificationMeta;
  };
}

interface UnreadCountApiResponse {
  success: boolean;
  response: { count: number };
}

export interface NotificationSettings {
  emailNotifications: boolean;
  pushNotifications: boolean;
  smsNotifications: boolean;
}

interface NotificationSettingsApiResponse {
  success: boolean;
  response: NotificationSettings;
}

export const notificationsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getNotifications: builder.query<
      { data: Notification[]; meta: NotificationMeta },
      { page?: number; limit?: number; isRead?: boolean }
    >({
      query: ({ page = 1, limit = 20, isRead } = {}) => ({
        url: '/notifications',
        params: {
          page,
          limit,
          ...(isRead !== undefined && { isRead }),
        },
      }),
      transformResponse: (response: NotificationsApiResponse) => ({
        data: response?.response?.data ?? [],
        meta: response?.response?.meta ?? {
          total: 0,
          page: 1,
          limit: 20,
          totalPages: 0,
          hasMore: false,
        },
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.data.map(({ id }) => ({ type: 'Notification' as const, id })),
              { type: 'Notification', id: 'LIST' },
            ]
          : [{ type: 'Notification', id: 'LIST' }],
    }),

    getUnreadCount: builder.query<number, void>({
      query: () => '/notifications/unread-count',
      transformResponse: (response: UnreadCountApiResponse) =>
        response?.response?.count ?? 0,
      providesTags: [{ type: 'Notification', id: 'UNREAD_COUNT' }],
      // Poll every 60 s so the badge stays fresh without manual refresh
      keepUnusedDataFor: 60,
    }),

    markNotificationAsRead: builder.mutation<void, string>({
      query: (notificationId) => ({
        url: `/notifications/${notificationId}/read`,
        method: 'PUT',
      }),
      // Optimistic update: flip isRead immediately in cached list
      onQueryStarted: async (notificationId, { dispatch, queryFulfilled, getState }) => {
        const state = getState() as any;
        // Patch every matching getNotifications cache entry
        const patches: ReturnType<typeof dispatch>[] = [];
        const queries = state.baseApi?.queries ?? {};
        for (const key of Object.keys(queries)) {
          if (!key.startsWith('getNotifications(')) continue;
          const patchResult = dispatch(
            notificationsApi.util.updateQueryData(
              'getNotifications',
              queries[key]?.originalArgs,
              (draft) => {
                const n = draft.data.find((x) => x.id === notificationId);
                if (n) n.isRead = true;
              }
            )
          );
          patches.push(patchResult);
        }
        try {
          await queryFulfilled;
        } catch {
          patches.forEach((p: any) => p.undo?.());
        }
      },
      invalidatesTags: [{ type: 'Notification', id: 'UNREAD_COUNT' }],
    }),

    markAllAsRead: builder.mutation<void, void>({
      query: () => ({
        url: '/notifications/read-all',
        method: 'PUT',
      }),
      invalidatesTags: [
        { type: 'Notification', id: 'LIST' },
        { type: 'Notification', id: 'UNREAD_COUNT' },
      ],
    }),

    deleteNotification: builder.mutation<void, string>({
      query: (notificationId) => ({
        url: `/notifications/${notificationId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_r, _e, id) => [
        { type: 'Notification', id },
        { type: 'Notification', id: 'LIST' },
        { type: 'Notification', id: 'UNREAD_COUNT' },
      ],
    }),

    getNotificationSettings: builder.query<NotificationSettings, void>({
      query: () => '/notifications/settings',
      transformResponse: (response: NotificationSettingsApiResponse) =>
        response?.response ?? {
          emailNotifications: false,
          pushNotifications: false,
          smsNotifications: false,
        },
      providesTags: [{ type: 'Notification', id: 'SETTINGS' }],
    }),

    updateNotificationSettings: builder.mutation<NotificationSettings, NotificationSettings>({
      query: (data) => ({
        url: '/notifications/settings',
        method: 'PUT',
        body: data,
      }),
      transformResponse: (response: NotificationSettingsApiResponse) =>
        response?.response ?? response as unknown as NotificationSettings,
      invalidatesTags: [{ type: 'Notification', id: 'SETTINGS' }],
    }),
  }),
});

export const {
  useGetNotificationsQuery,
  useGetUnreadCountQuery,
  useMarkNotificationAsReadMutation,
  useMarkAllAsReadMutation,
  useDeleteNotificationMutation,
  useGetNotificationSettingsQuery,
  useUpdateNotificationSettingsMutation,
} = notificationsApi;
